import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { StediPayerSearch } from '@features/payer-search/components/StediPayerSearch';
import {
  payerSearchApi,
  useGetAdminPayerByIdQuery,
  useGetAdminPayersQuery,
} from '@features/payer-search/store/api';
import type { Payer } from '@features/payer-search/types';
import { useDebounce } from '@hooks/useDebounce';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useAppDispatch } from '@store/hooks';

import { type IntakeRosterPayer, useAdminIntakePayerRoster } from '../../hooks/useIntakePayerRoster';
import {
  useCreateCredentialingStatusMutation,
  useGetAdminCredentialingStatusesQuery,
  useUpdateCredentialingStatusMutation,
} from '../../store/api';
import type {
  AdminCredentialingStatus,
  CredentialingRejectionReason,
  CredentialingStatus,
  PublicCredentialingStatus,
  UpdateCredentialingStatusRequest,
} from '../../types';
import { footnoteSx, overlineSx, wf } from '../shell/tokens';

import {
  type CredentialingPayerJumpEntry,
  CredentialingPayerJumpStrip,
} from './CredentialingPayerJumpStrip';
import { AdminCredentialingPayerName, CredentialingPayerLabel } from './CredentialingPayerName';
import type { CredentialingMatrixCell, CredentialingMatrixGroup } from './CredentialingRosterMatrix';
import { CredentialingRosterMatrix } from './CredentialingRosterMatrix';
import {
  CREDENTIALING_PAGE_SIZE,
  CREDENTIALING_REJECTION_REASON_LABELS,
  OFF_ROSTER_BLURB,
  OFF_ROSTER_TITLE,
  ROSTER_CAPTION,
  UNRESOLVED_PAYER_NOTICE,
} from './credentialingStatus';
import { CredentialingStatusPanel, CredentialingStatusSelect } from './CredentialingStatusPanel';
import { matchRosterPayer, useCredentialingPayerIdentities } from './useCredentialingPayerIdentities';

/**
 * The app tsconfig types `dispatch` without the injected-endpoint thunk
 * overload, so the subscription is asserted to the documented RTK contract.
 */
interface QuerySubscription<T> {
  unwrap: () => Promise<T>;
  unsubscribe: () => void;
}

interface DirectorySearchItem {
  id?: string | null;
  stedi_id: string;
  primary_payer_id?: string | null;
}

export interface CredentialingProviderOption {
  id: string;
  label: string;
}

const NO_ROWS: AdminCredentialingStatus[] = [];

/** Where a jump lands: highlighted so staff see which rows it brought into view. */
const jumpFocusSx = {
  borderRadius: '4px',
  '&:focus': { outline: `2px solid ${wf.blue}`, outlineOffset: '2px' },
} as const;

/** A name for the jump strip: no Payer ID and no retry control, since it sits inside a button. */
function AdminPayerDisplayName({ id }: { id: string }) {
  const { currentData, isFetching } = useGetAdminPayerByIdQuery({ payerId: id });
  if (currentData?.display_name) return <>{currentData.display_name}</>;
  return <>{isFetching ? 'Loading payer…' : 'Payer name unavailable'}</>;
}

/** The owning route supplies its evaluated V3 gate; this component also checks staff permissions. */
export function AdminCredentialingStatuses({
  organizationId,
  userId,
  enabled,
  providers,
  providerNotice,
  onDirtyChange,
}: {
  organizationId: string;
  userId?: string;
  enabled: boolean;
  providers: CredentialingProviderOption[];
  providerNotice?: ReactNode;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const roles: unknown = user?.['https://api.smartscribe.health/roles'];
  const admin = Array.isArray(roles) && roles.includes('Admin');
  const canRead = admin || (Array.isArray(roles) && roles.includes('Moderator'));
  if (!enabled || !organizationId || isLoading || !isAuthenticated || !canRead) return null;
  return (
    <ScopedAdminCredentialingStatuses
      key={JSON.stringify([organizationId, userId, user?.sub, admin])}
      organizationId={organizationId}
      userId={userId}
      providers={providers}
      providerNotice={providerNotice}
      onDirtyChange={onDirtyChange}
      canWrite={admin}
    />
  );
}

function ScopedAdminCredentialingStatuses({
  organizationId,
  userId,
  providers,
  providerNotice,
  onDirtyChange,
  canWrite,
}: {
  organizationId: string;
  userId?: string;
  providers: CredentialingProviderOption[];
  providerNotice?: ReactNode;
  onDirtyChange?: (dirty: boolean) => void;
  canWrite: boolean;
}) {
  const dispatch = useAppDispatch();
  const [offset, setOffset] = useState(0);
  const [provider, setProvider] = useState(userId ?? '');
  const [payer, setPayer] = useState<Payer | null>(null);
  const [search, setSearch] = useState('');
  const term = useDebounce(search.trim(), 300);
  const directory = useGetAdminPayersQuery(
    { search: term, perPage: 50 },
    { skip: !canWrite || term.length < 2 },
  );
  const [status, setStatus] = useState<CredentialingStatus>('not_started');
  const [message, setMessage] = useState<{ error: boolean; text: string }>();
  const query = useGetAdminCredentialingStatusesQuery(
    { organizationId, userId, limit: CREDENTIALING_PAGE_SIZE, offset },
    { refetchOnMountOrArgChange: true },
  );
  const [create, creating] = useCreateCredentialingStatusMutation();
  const [update, updating] = useUpdateCredentialingStatusMutation();
  const saving = creating.isLoading || updating.isLoading;
  const draftKey = JSON.stringify([provider, payer?.id ?? '', search, status]);
  const [savedDraftKey, setSavedDraftKey] = useState(
    JSON.stringify([userId ?? '', '', '', 'not_started']),
  );
  useEffect(() => {
    onDirtyChange?.(saving || draftKey !== savedDraftKey);
  }, [draftKey, savedDraftKey, saving, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  /** The current page's records, or undefined until they have loaded. */
  const fetched = query.currentData?.items;
  const wrongScope = (fetched ?? []).some(
    row => row.organization_id !== organizationId || (userId && row.user_id !== userId),
  );
  const failed = query.isError || wrongScope;

  // The intake form's answer is the list; the records only decorate it.
  const roster = useAdminIntakePayerRoster(organizationId, userId);
  const payerUuids = useMemo(
    () => (failed ? [] : (fetched ?? []).map(row => row.payer_uuid)),
    [failed, fetched],
  );
  const { identities, pending: identitiesPending } = useCredentialingPayerIdentities(
    payerUuids,
    'admin',
    roster.payers.length > 0,
  );
  // Until the identity join lands, a record cannot be placed on its roster
  // row; showing it now would flash it under "Not on the roster".
  const identitiesSettling = roster.payers.length > 0 && identitiesPending;
  // The rows on screen are the last set whose identities settled. A refetch
  // after a save (and the identity lookup for a record it created) therefore
  // leaves the table as it was rather than swapping it for a progress bar,
  // which collapsed the page and made it jump on every status change.
  const [settled, setSettled] = useState<AdminCredentialingStatus[]>();
  if (fetched && !identitiesSettling && settled !== fetched) setSettled(fetched);
  const rows = settled ?? NO_ROWS;
  const loading = !failed && settled === undefined;
  const refreshing = !loading && !failed && (query.isFetching || identitiesSettling);
  /** Row controls wait for the fresh rows too; `saving` alone still drives the dirty guard. */
  const busy = saving || refreshing;
  /** Roster key for a record, or null when its payer is not on the roster. */
  const rosterKeyOf = useMemo(() => {
    const byUuid: Record<string, string | null> = {};
    for (const uuid of new Set(payerUuids))
      byUuid[uuid] = matchRosterPayer(identities[uuid], roster.payers)?.key ?? null;
    return byUuid;
  }, [payerUuids, identities, roster.payers]);
  /** A record already proves a roster payer's directory id; no search needed. */
  const uuidFromRecords = useMemo(() => {
    const byKey: Record<string, string> = {};
    for (const row of rows) {
      const key = rosterKeyOf[row.payer_uuid];
      if (key && !byKey[key]) byKey[key] = row.payer_uuid;
    }
    return byKey;
  }, [rows, rosterKeyOf]);
  const [resolvedUuids, setResolvedUuids] = useState<Record<string, string>>({});
  const [unresolved, setUnresolved] = useState<Record<string, true>>({});
  /** The elements the jump strip scrolls to: a payer's label, or a rejected record's detail. */
  const jumpTargets = useRef(new Map<string, HTMLElement>());
  // The table repeats a payer's label on each of its rows; the first one is the landing.
  const jumpTarget = (key: string) => (node: HTMLElement | null) => {
    if (!node) jumpTargets.current.delete(key);
    else if (!jumpTargets.current.has(key)) jumpTargets.current.set(key, node);
  };
  const payerAnchor = (key: string, label: ReactNode) => (
    <Box component="span" ref={jumpTarget(`payer:${key}`)} tabIndex={-1} sx={{ ...jumpFocusSx, display: 'block' }}>
      {label}
    </Box>
  );

  const providerAxis = userId ? providers.filter(item => item.id === userId) : providers;
  const providerLabel = (id: string) => providers.find(item => item.id === id)?.label ?? id;
  const rosterDriven = !failed && roster.payers.length > 0 && providerAxis.length > 0;

  const updateRow = async (row: PublicCredentialingStatus, nextStatus: CredentialingStatus) => {
    if (!canWrite || saving || row.organization_id !== organizationId) return;
    setMessage(undefined);
    try {
      const result = await update({
        organizationId,
        credentialingStatusId: row.id,
        updateCredentialingStatusRequest: { status: nextStatus },
      }).unwrap();
      if (result.organization_id !== organizationId || (userId && result.user_id !== userId))
        throw new Error('Scope mismatch');
      setMessage({ error: false, text: 'Credentialing status saved.' });
    } catch {
      setMessage({
        error: true,
        text: 'We couldn’t save this status. Refresh to check for an existing record before retrying.',
      });
    }
  };

  const saveRejection = async (
    row: AdminCredentialingStatus,
    changes: Pick<UpdateCredentialingStatusRequest, 'rejection_reason' | 'rejection_note'>,
  ) => {
    if (!canWrite || saving || row.organization_id !== organizationId) return;
    setMessage(undefined);
    try {
      await update({
        organizationId,
        credentialingStatusId: row.id,
        updateCredentialingStatusRequest: changes,
      }).unwrap();
      setMessage({ error: false, text: 'Rejection details saved.' });
    } catch {
      setMessage({
        error: true,
        text: 'We couldn’t save the rejection details. Refresh and retry.',
      });
    }
  };
  const rejectionDetails = (record: PublicCredentialingStatus) => {
    const row = rows.find(item => item.id === record.id);
    if (!row || row.status !== 'rejected') return null;
    const note = row.rejection_note ?? '';
    const subject = `${row.user_id} / ${row.payer_uuid}`;
    return (
      <Stack
        ref={jumpTarget(`record:${row.id}`)}
        tabIndex={-1}
        spacing={1}
        sx={{ ...jumpFocusSx, mt: 1, minWidth: 240 }}
      >
        <TextField
          select
          size="small"
          label="Rejection reason"
          value={row.rejection_reason ?? ''}
          disabled={!canWrite || busy}
          onChange={event => {
            const reason = event.target.value;
            if (reason in CREDENTIALING_REJECTION_REASON_LABELS)
              void saveRejection(row, {
                rejection_reason: reason as CredentialingRejectionReason,
              });
          }}
          slotProps={{
            select: {
              SelectDisplayProps: {
                'aria-label': `Rejection reason for ${subject}`,
                'aria-labelledby': undefined,
              },
            },
          }}
        >
          {Object.entries(CREDENTIALING_REJECTION_REASON_LABELS).map(([reason, text]) => (
            <MenuItem key={reason} value={reason}>
              {text}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          // Remount when the saved value changes so the draft never goes stale.
          key={note}
          size="small"
          multiline
          label="Note"
          placeholder="Add detail about this rejection"
          defaultValue={note}
          disabled={!canWrite || busy}
          onBlur={event => {
            const next = event.target.value.trim();
            if (next !== note) void saveRejection(row, { rejection_note: next });
          }}
          slotProps={{ htmlInput: { 'aria-label': `Rejection note for ${subject}` } }}
        />
      </Stack>
    );
  };

  const createRecord = async (
    providerId: string,
    payerUuid: string,
    nextStatus: CredentialingStatus,
    markDraftSaved = false,
  ) => {
    if (!canWrite || saving) return;
    setMessage(undefined);
    try {
      const result = await create({
        organizationId,
        createCredentialingStatusRequest: {
          user_id: providerId,
          payer_uuid: payerUuid,
          status: nextStatus,
        },
      }).unwrap();
      if (result.organization_id !== organizationId || (userId && result.user_id !== userId))
        throw new Error('Scope mismatch');
      if (markDraftSaved) setSavedDraftKey(draftKey);
      setMessage({ error: false, text: 'Credentialing status saved.' });
    } catch {
      setMessage({
        error: true,
        text: 'We couldn’t save this status. Refresh to check for an existing record before retrying.',
      });
    }
  };

  /** The add-a-record form: the payer is already a directory row, so no join. */
  const save = async () => {
    const payerId = payer?.id;
    if (!payerId || !providers.some(item => item.id === provider)) return;
    await createRecord(provider, payerId, status, true);
  };

  /**
   * The roster stores a Stedi id and the payer's own id; a credentialing record
   * stores a directory UUID. With no record to borrow one from, the UUID has to
   * be found by searching the directory for the roster payer's name and matching
   * on Stedi id — the only handle both sides share.
   */
  const resolveRosterPayerUuid = async (rosterPayer: IntakeRosterPayer) => {
    const known = uuidFromRecords[rosterPayer.key] ?? resolvedUuids[rosterPayer.key];
    if (known) return known;
    const request = dispatch(
      payerSearchApi.endpoints.getV2AdminPayers.initiate({
        search: rosterPayer.payerName,
        perPage: 50,
      }),
    ) as unknown as QuerySubscription<{ items: DirectorySearchItem[] }>;
    try {
      const result = await request.unwrap();
      const match = result.items.find(item =>
        rosterPayer.stediId
          ? item.stedi_id === rosterPayer.stediId
          : item.primary_payer_id === rosterPayer.payerId,
      );
      if (match?.id) {
        const uuid = match.id;
        setResolvedUuids(current => ({ ...current, [rosterPayer.key]: uuid }));
        return uuid;
      }
    } catch {
      // Falls through to the inline notice below.
    } finally {
      request.unsubscribe();
    }
    setUnresolved(current => ({ ...current, [rosterPayer.key]: true }));
    return null;
  };

  const onMatrixChange = (
    group: CredentialingMatrixGroup,
    cell: CredentialingMatrixCell,
    nextStatus: CredentialingStatus,
  ) => {
    if (cell.record) {
      void updateRow(cell.record, nextStatus);
      return;
    }
    const rosterPayer = roster.payers.find(item => item.key === group.key);
    if (!rosterPayer) return;
    void (async () => {
      const uuid = await resolveRosterPayerUuid(rosterPayer);
      if (uuid) await createRecord(cell.providerId, uuid, nextStatus);
    })();
  };

  const rosterGroups: CredentialingMatrixGroup[] = roster.payers.map(rosterPayer => ({
    key: rosterPayer.key,
    payerLabel: payerAnchor(
      rosterPayer.key,
      <CredentialingPayerLabel name={rosterPayer.payerName} payerId={rosterPayer.payerId} />,
    ),
    caption: ROSTER_CAPTION,
    // The notice sends staff to the add form below; once a record proves the
    // payer's directory id, the cells have to become editable again.
    notice:
      unresolved[rosterPayer.key] && !uuidFromRecords[rosterPayer.key]
        ? UNRESOLVED_PAYER_NOTICE
        : undefined,
    cells: providerAxis.map(item => ({
      providerId: item.id,
      providerLabel: item.label,
      record: rows.find(
        row => row.user_id === item.id && rosterKeyOf[row.payer_uuid] === rosterPayer.key,
      ),
    })),
  }));
  /** Records the roster does not explain still have to be visible and editable. */
  const offRosterGroups: CredentialingMatrixGroup[] = [];
  for (const row of rows) {
    if (rosterKeyOf[row.payer_uuid]) continue;
    const existing = offRosterGroups.find(group => group.key === row.payer_uuid);
    const cell = {
      providerId: row.user_id,
      providerLabel: providerLabel(row.user_id),
      record: row,
    };
    const identity = identities[row.payer_uuid];
    if (existing) existing.cells.push(cell);
    else
      offRosterGroups.push({
        key: row.payer_uuid,
        payerLabel: payerAnchor(
          row.payer_uuid,
          identity?.displayName != null ? (
            <CredentialingPayerLabel
              name={identity.displayName}
              payerId={identity.primaryPayerId}
            />
          ) : (
            <AdminCredentialingPayerName id={row.payer_uuid} />
          ),
        ),
        cells: [cell],
      });
  }

  /**
   * One entry per payer, in the order the records below list them, tallied
   * from the rows on screen. `rejectedId` is the first rejection shown for it.
   */
  const jumpEntries: (CredentialingPayerJumpEntry & { rejectedId?: string })[] = [];
  const fromGroup = (group: CredentialingMatrixGroup, name: ReactNode) =>
    jumpEntries.push({
      key: group.key,
      name,
      statuses: group.cells.map(cell => cell.record?.status ?? 'not_started'),
      rejectedId: group.cells.find(cell => cell.record?.status === 'rejected')?.record?.id,
    });
  if (rosterDriven) {
    rosterGroups.forEach((group, index) => fromGroup(group, roster.payers[index].payerName));
    for (const group of offRosterGroups)
      fromGroup(
        group,
        identities[group.key]?.displayName ?? <AdminPayerDisplayName id={group.key} />,
      );
  } else {
    for (const row of rows) {
      const existing = jumpEntries.find(entry => entry.key === row.payer_uuid);
      const rejectedId = row.status === 'rejected' ? row.id : undefined;
      if (existing) {
        existing.statuses.push(row.status);
        existing.rejectedId ??= rejectedId;
      } else
        jumpEntries.push({
          key: row.payer_uuid,
          name: <AdminPayerDisplayName id={row.payer_uuid} />,
          statuses: [row.status],
          rejectedId,
        });
    }
  }
  /** A payer with a rejection lands on its first rejection's reason and note; any other on its rows. */
  const jumpTo = (key: string) => {
    const rejectedId = jumpEntries.find(entry => entry.key === key)?.rejectedId;
    const target =
      (rejectedId && jumpTargets.current.get(`record:${rejectedId}`)) ||
      jumpTargets.current.get(`payer:${key}`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target?.focus({ preventScroll: true });
  };

  return (
    <CredentialingStatusPanel
      rows={failed ? [] : rows}
      pending={loading}
      refreshing={refreshing}
      failed={failed}
      offset={offset}
      onPage={setOffset}
      onRetry={() => void query.refetch()}
      providerLabel={providerLabel}
      payerLabel={id =>
        payerAnchor(
          id,
          payer?.id === id ? (
            <CredentialingPayerLabel name={payer.display_name} payerId={payer.primary_payer_id} />
          ) : (
            <AdminCredentialingPayerName id={id} />
          ),
        )
      }
      onUpdate={canWrite ? (row, value) => void updateRow(row, value) : undefined}
      rowExtra={rejectionDetails}
      saving={busy}
      body={
        rosterDriven ? (
          <CredentialingRosterMatrix
            groups={rosterGroups}
            trailingGroups={offRosterGroups}
            trailingTitle={OFF_ROSTER_TITLE}
            trailingBlurb={OFF_ROSTER_BLURB}
            onChange={canWrite ? onMatrixChange : undefined}
            rowExtra={rejectionDetails}
            saving={busy}
          />
        ) : undefined
      }
    >
      {!loading && !failed && jumpEntries.length > 0 && (
        <CredentialingPayerJumpStrip entries={jumpEntries} onJump={jumpTo} />
      )}
      {providerNotice}
      {rosterDriven && rows.length >= CREDENTIALING_PAGE_SIZE && (
        <Alert severity="info">
          This page shows {CREDENTIALING_PAGE_SIZE} records. A pair whose record is on another page
          reads “Not started” here — use Next to see the rest before changing it.
        </Alert>
      )}
      {canWrite && (
        <Stack
          spacing={2}
          sx={{
            border: `1px solid ${wf.line}`,
            borderRadius: '10px',
            backgroundColor: wf.surfaceAlt,
            padding: '16px 18px',
          }}
        >
          <Box>
            <Typography sx={overlineSx}>
              {rosterDriven ? 'Add a payer that is not on the roster' : 'Add a record'}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: wf.muted, lineHeight: 1.55, mt: '4px' }}>
              One record per provider and payer. Each status saves on change, in any direction.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Provider"
              value={provider}
              onChange={event => setProvider(event.target.value)}
              disabled={saving || Boolean(userId)}
              sx={{ minWidth: 220 }}
            >
              {providers.map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <Stack sx={{ minWidth: 280, flex: 1 }}>
              <StediPayerSearch
                label="Payer"
                disabled={saving}
                inputValue={search}
                onInputChange={value => {
                  setSearch(value);
                  setPayer(null);
                }}
                loading={directory.isFetching}
                error={directory.isError}
                options={(directory.currentData?.items ?? [])
                  .filter(item => item.id)
                  .map(item => ({
                    displayName: item.display_name,
                    stediId: item.stedi_id,
                    payerId: item.primary_payer_id,
                  }))}
                value={payer ? { displayName: payer.display_name, stediId: payer.stedi_id } : null}
                onSelect={option => {
                  const selected =
                    directory.currentData?.items.find(item => item.stedi_id === option?.stediId) ??
                    null;
                  setPayer(selected);
                  setSearch(selected?.display_name ?? '');
                }}
              />
            </Stack>
            <CredentialingStatusSelect value={status} onChange={setStatus} disabled={saving} />
          </Stack>
          <Button
            variant="contained"
            disabled={saving || !provider || !payer?.id}
            onClick={() => void save()}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add credentialing status
          </Button>
        </Stack>
      )}
      {message && <Alert severity={message.error ? 'error' : 'success'}>{message.text}</Alert>}
      <Typography sx={footnoteSx}>
        {rosterDriven
          ? 'Every payer the practice marked “Wants to get credentialed” on the intake form is listed here, whether or not a status has been recorded. The practice-level status the client sees is derived from these records: complete when every record that was not rejected is complete, in progress when any record has moved, otherwise not started.'
          : 'The practice-level status the client sees is derived from these records: complete when every record that was not rejected is complete, in progress when any record has moved, otherwise not started.'}
      </Typography>
    </CredentialingStatusPanel>
  );
}
